
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Activity, Award, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardData } from '@/hooks/use-dashboard-data';

interface ActivityListProps {
  isLoading: boolean;
  activities: DashboardData['recentActivities'];
}

export const ActivityList = ({ isLoading, activities }: ActivityListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-lg animate-pulse bg-secondary/20" style={{ height: "60px" }} />
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <Activity className="h-12 w-12 mb-4 opacity-20" />
        <p>No recent activities</p>
        <p className="text-sm mt-1">Complete a workout to see your progress</p>
      </div>
    );
  }

  return (
    <ul className="space-y-4">
      {activities.map((activity) => (
        <motion.li
          key={activity.id}
          whileHover={{ x: 5 }}
          className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">{activity.title}</p>
              <p className="text-sm text-muted-foreground">{activity.time}</p>
            </div>
          </div>
          <div className="flex items-center">
            <span className="text-sm font-medium rounded-full bg-primary/10 px-3 py-1 text-primary">
              +{activity.points} pts
            </span>
          </div>
        </motion.li>
      ))}
    </ul>
  );
};

export const ActivityCard = ({ isLoading, activities }: ActivityListProps) => {
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-lexend flex items-center">
          <Activity className="h-5 w-5 mr-2 text-primary" />
          Recent Activity
        </CardTitle>
        <CardDescription>Your latest AI learning activities</CardDescription>
      </CardHeader>
      <CardContent>
        <ActivityList isLoading={isLoading} activities={activities} />
      </CardContent>
      <CardFooter>
        <Button variant="ghost" asChild className="w-full">
          <Link to="/activity" className="flex items-center justify-center">
            <Plus className="h-4 w-4 mr-2" />
            View All Activity
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};
